import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, FormGroup, Label } from 'reactstrap';
import api from '../../api';
import jwt_decode from 'jwt-decode';
import './PlayerShopModals.css';

const PlayerShopManageModal = ({ isOpen, toggle, lotId }) => {
  // Get user info from JWT token
  const authUser = (() => {
    try {
      // Try to get username from JWT token first
      const token = localStorage.getItem("jwtToken");
      if (token) {
        const decoded = jwt_decode(token);
        if (decoded.sub || decoded.username) {
          return {
            username: decoded.sub || decoded.username,
            id: decoded.id || null
          };
        }
      }
      
      // Fallback to authUser if token is invalid or not present
      const user = localStorage.getItem("authUser");
      return user ? JSON.parse(user) : { username: "", id: null };
    } catch (error) {
      return { username: "", id: null };
    }
  })();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [tab, setTab] = useState('inventory'); // 'inventory' or 'shop'
  
  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const fetchShopItems = useCallback(async () => {
    try {
      setLoading(true);
      // Use authUser.id to verify ownership
      const response = await api.get(`/v1/player-shops/lot/${lotId}/items`, { 
        withCredentials: true,
        params: { userId: authUser.id } // Pass user ID as a parameter
      });
      setShopItems(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching shop items:', err);
      setError('Failed to load shop items. Please try again.');
      setLoading(false);
    }
  }, [lotId, authUser.id]);

  const fetchInventory = useCallback(async () => {
    try {
      const response = await api.get('/v1/inventory', { withCredentials: true });
      
      if (Array.isArray(response.data)) {
        // Filter out pickaxes and items with 0 quantity
        const filteredItems = response.data.filter(item => {
          // Exclude items with 0 quantity
          if (item.amount <= 0) return false;
          
          // Exclude pickaxes (check name or item_name for 'pickaxe')
          const itemName = (item.name || item.item_name || '').toLowerCase();
          return !itemName.includes('pickaxe');
        });
        
        setInventory(filteredItems || []);
      } else {
        setInventory([]);
      }
    } catch (err) {
      setError('Failed to load inventory. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (isOpen && lotId) {
      fetchShopItems();
      fetchInventory();
    }
  }, [isOpen, lotId, fetchShopItems, fetchInventory]);

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setQuantity(1);
    setPrice(0);
  };

  const handlePriceChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setPrice(Math.max(0, value));
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    if (selectedItem) {
      setQuantity(Math.max(1, Math.min(selectedItem.amount, value)));
    } else {
      setQuantity(Math.max(1, value));
    }
  };

  const handleListItem = async () => {
    if (!selectedItem || price <= 0) {
      setError('Please select an item and set a valid price.');
      return;
    }
    
    if (!lotId) {
      setError('Shop lot ID is missing. Please try again.');
      return;
    }

    if (!authUser.id) {
      setError('User authentication issue. Please try logging out and back in.');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/v1/player-shops/lot/${lotId}/list-item`, {
        item_id: selectedItem.item,
        price,
        quantity,
        user_id: authUser.id // Include user ID in the request
      }, { withCredentials: true });
      setSelectedItem(null);
      setPrice(0);
      setQuantity(1);
      setLoading(false);
      // Refresh data
      fetchShopItems();
      fetchInventory();
    } catch (err) {
      console.error('Error listing item:', err);
      setError(err.response?.data?.detail || 'Failed to list item. Please try again.');
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      setLoading(true);
      await api.delete(`/v1/player-shops/item/${itemId}`, { withCredentials: true });
      
      // Refresh data
      fetchShopItems();
      fetchInventory();
      setLoading(false);
    } catch (err) {
      console.error('Error removing item:', err);
      setError(err.response?.data?.detail || 'Failed to remove item. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="player-shop-manage-modal" size="lg">
      <ModalHeader toggle={toggle} style={{ backgroundColor: '#1d222e', color: '#fff', borderBottom: '1px solid #2a3042' }} className="modal-colored-header">
        {authUser.username}'s Shop (Lot ID: {lotId})
      </ModalHeader>
      <ModalBody style={{ backgroundColor: '#1d222e', color: '#fff' }} className="modal-colored-body">
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}
        
        <div className="shop-tabs">
          <div className="tab-buttons">
            <button 
              className={`tab-button ${tab === 'inventory' ? 'active' : ''}`}
              onClick={() => setTab('inventory')}
            >
              List Items for Sale
            </button>
            <button 
              className={`tab-button ${tab === 'shop' ? 'active' : ''}`}
              onClick={() => setTab('shop')}
            >
              Current Shop Items
            </button>
          </div>
          
          <div className="tab-content">
            {tab === 'inventory' ? (
              <div className="inventory-tab">
                <h5>Select an Item to Sell</h5>
                {loading ? (
                  <p>Loading inventory...</p>
                ) : inventory.length === 0 ? (
                  <div>
                    <p>Your inventory is empty. You need items in your inventory to list them for sale.</p>
                    <Button color="primary" onClick={fetchInventory}>Refresh Inventory</Button>
                  </div>
                ) : (
                  <>
                    <div className="inventory-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
                      {inventory.map(item => (
                        <div 
                          key={item.item} 
                          className={`inventory-item ${selectedItem?.item === item.item ? 'selected' : ''}`}
                          onClick={() => handleItemSelect(item)}
                          style={{
                            border: selectedItem?.item === item.item ? '2px solid #007bff' : '1px solid #2a3042',
                            borderRadius: '8px',
                            padding: '10px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: selectedItem?.item === item.item ? '#2a3042' : '#1d222e',
                            position: 'relative'
                          }}
                        >
                          <div className="item-image">
                            <img 
                              src={item.image || item.item_image} 
                              alt={item.name || item.item_name} 
                              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                            />
                            <span className="item-amount" style={{ 
                              position: 'absolute', 
                              right: '5px', 
                              top: '5px', 
                              backgroundColor: '#007bff', 
                              color: 'white',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px'
                            }}>{item.amount}</span>
                          </div>
                          <div className="item-name" style={{ marginTop: '5px', fontSize: '12px', color: '#fff' }}>{item.name || item.item_name}</div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedItem && (
                      <div className="listing-form" style={{ backgroundColor: '#2a3042', padding: '15px', borderRadius: '8px' }}>
                        <h6 style={{ color: '#fff' }}>Listing Details for {selectedItem.name || selectedItem.item_name}</h6>
                        <FormGroup>
                          <Label for="price" style={{ color: '#fff' }}>Price per Item (coins)</Label>
                          <Input
                            type="number"
                            id="price"
                            value={price}
                            onChange={handlePriceChange}
                            min="1"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="quantity" style={{ color: '#fff' }}>Quantity (max: {selectedItem.amount})</Label>
                          <Input
                            type="number"
                            id="quantity"
                            value={quantity}
                            onChange={handleQuantityChange}
                            min="1"
                            max={selectedItem.amount}
                          />
                        </FormGroup>
                        <div className="total-calculation" style={{ backgroundColor: '#1d222e', padding: '10px', borderRadius: '4px', marginBottom: '15px', color: '#fff' }}>
                          <strong>Total Value:</strong> <img src="items/coin.png" alt="Coin" style={{ height: '16px', marginRight: '5px' }} /> {formatNumber(price * quantity)} coins
                        </div>
                        <Button 
                          color="primary" 
                          onClick={handleListItem}
                          disabled={loading || !selectedItem || price <= 0}
                        >
                          {loading ? 'Processing...' : 'List Item for Sale'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="shop-tab">
                <h5 style={{ color: '#fff' }}>Your Shop Items</h5>
                <div>
                  <Button color="primary" onClick={fetchShopItems} className="mb-2">
                    Refresh Items
                  </Button>
                </div>
                {loading ? (
                  <p>Loading shop items...</p>
                ) : shopItems.length === 0 ? (
                  <div style={{ color: '#fff' }}>
                    <p>You don't have any items for sale.</p>
                    <p>Lot ID: {lotId}</p>
                    <p>Go to the "List Items for Sale" tab to add items to your shop.</p>
                  </div>
                ) : (
                  <Table responsive dark bordered style={{ backgroundColor: '#1d222e', color: '#fff' }}>
                    <thead style={{ backgroundColor: '#2a3042' }}>
                      <tr>
                        <th>Item</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Total Value</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shopItems.map(item => (
                        <tr key={item.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <img src={item.item_image} alt={item.item_name} style={{ height: '32px', marginRight: '10px' }} />
                              <span style={{ color: '#fff' }}>{item.item_name}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <img src="items/coin.png" alt="Coin" style={{ height: '16px', marginRight: '5px' }} />
                              <span style={{ color: '#28a745' }}>{formatNumber(item.price)}</span>
                            </div>
                          </td>
                          <td style={{ color: '#fff' }}>{formatNumber(item.quantity)}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <img src="items/coin.png" alt="Coin" style={{ height: '16px', marginRight: '5px' }} />
                              <span style={{ color: '#28a745' }}>{formatNumber(item.price * item.quantity)}</span>
                            </div>
                          </td>
                          <td>
                            <Button 
                              color="danger" 
                              size="sm" 
                              onClick={() => handleRemoveItem(item.id)}
                              disabled={loading}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter style={{ backgroundColor: '#1d222e', color: '#fff', borderTop: '1px solid #2a3042' }} className="modal-colored-footer">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PlayerShopManageModal;
